import { ThemeMode } from "@rneui/themed";
import memoize from "fast-memoize";
import React, { useEffect, useMemo, useRef } from "react";
import { Platform, StyleSheet, useWindowDimensions } from "react-native";
import { ThemeOptions, useTheme } from "react-native-elements";
import { color } from "react-native-elements/dist/config";
import { Directions, Gesture } from "react-native-gesture-handler";
import { getEndingData } from "../../backend/endings/Endings";
import { hasFlag, hasGlobalFlag } from "../../backend/Flags";
import { isInventoryUnlocked } from "../../backend/items/Items";
import { PlayerContextState } from "../../backend/PlayerContext";
import {
  getTotalElementAndSchoolExp,
  SpellElement,
} from "../../backend/spells/ElementsAndSchools";
import { Markdown } from "./Markdown";

const disabledColorByThemeMemoized = memoize(
  (themeMode: string, disabledColor?: string) => {
    return themeMode === "dark"
      ? color(disabledColor).lighten(1.2).string()
      : color(disabledColor).darken(0.3).string();
  },
);

export function disabledColorByTheme(theme: ThemeOptions) {
  return disabledColorByThemeMemoized(theme.mode, theme?.colors?.disabled);
}

const LIGHT_ELEMENT_COLORS = {
  Fire: "#c30",
  Earth: "#740",
  Water: "#08f",
  Air: "#0b4",
  Nature: "#5b0",
  Lightning: "#80f",
  Darkness: "#444",
};

const DARK_ELEMENT_COLORS = {
  Fire: "#820",
  Earth: "#630",
  Water: "#048",
  Air: "#063",
  Nature: "#360",
  Lightning: "#529",
  Darkness: "#444",
};

export function showImportFromFile(): boolean {
  return Platform.select({
    ios: true,
    android: true,
    default: false,
  });
}

export function showExportToFile(): boolean {
  return Platform.select({
    ios: true,
    android: true,
    web: true,
    default: false,
  });
}

export function elementColorByTheme(
  themeMode: ThemeMode,
  element: SpellElement,
) {
  return themeMode === "dark"
    ? DARK_ELEMENT_COLORS[element]
    : LIGHT_ELEMENT_COLORS[element];
}

export function getPrimaryButtonTextColor(
  theme: ThemeOptions,
  disabled: boolean,
): string {
  if (disabled) {
    return disabledColorByTheme(theme);
  }
  return "white";
}

export function DescriptionViewer(props: any) {
  const theme = useTheme();
  return (
    <Markdown style={[{ color: theme.theme.colors.grey1 }, props.style || {}]}>
      {props.children}
    </Markdown>
  );
}

export function EffectViewer(props: any) {
  const theme = useTheme();
  return (
    <Markdown
      style={[
        styles.effectText,
        { color: theme.theme.colors.grey1 },
        props.style || {},
      ]}
    >
      {props.children}
    </Markdown>
  );
}

export type SectionData = {
  name: string;
  scrollable: boolean;
  hideBottomResourceBar: boolean;
  scrollView: boolean;
};

export function getAllSections(): SectionData[] {
  return [
    {
      name: "Campus",
      scrollable: true,
      hideBottomResourceBar: false,
      scrollView: false,
    },
    {
      name: "Research",
      scrollable: true,
      hideBottomResourceBar: false,
      scrollView: true,
    },
    {
      name: "Apprentices",
      scrollable: true,
      hideBottomResourceBar: true,
      scrollView: false,
    },
    {
      name: "Inventory",
      scrollable: true,
      hideBottomResourceBar: true,
      scrollView: true,
    },
    {
      name: "Exploration",
      scrollable: false,
      hideBottomResourceBar: true,
      scrollView: false,
    },
    {
      name: "Upgrades",
      scrollable: true,
      hideBottomResourceBar: false,
      scrollView: true,
    },
    {
      name: "Creatures",
      scrollable: true,
      hideBottomResourceBar: false,
      scrollView: true,
    },
    {
      name: "Strengthen",
      scrollable: true,
      hideBottomResourceBar: false,
      scrollView: true,
    },
    {
      name: "Retirement",
      scrollable: true,
      hideBottomResourceBar: true,
      scrollView: true,
    },
    {
      name: "Ending",
      scrollable: true,
      hideBottomResourceBar: true,
      scrollView: true,
    },
    {
      name: "Creation",
      scrollable: true,
      hideBottomResourceBar: true,
      scrollView: true,
    },
    {
      name: "Stats",
      scrollable: true,
      hideBottomResourceBar: true,
      scrollView: true,
    },
    {
      name: "Bestiary",
      scrollable: true,
      hideBottomResourceBar: true,
      scrollView: true,
    },
    {
      name: "Options",
      scrollable: true,
      hideBottomResourceBar: true,
      scrollView: true,
    },
    {
      name: "About",
      scrollable: true,
      hideBottomResourceBar: true,
      scrollView: true,
    },
    {
      name: "Help",
      scrollable: true,
      hideBottomResourceBar: true,
      scrollView: true,
    },
    {
      name: "Debug",
      scrollable: true,
      hideBottomResourceBar: true,
      scrollView: true,
    },
  ];
}

export function getAllMenuItems(playerContext: PlayerContextState): string[] {
  const showResearch = hasFlag(playerContext, "research_unlocked");
  const showAutocast = hasFlag(playerContext, "autocast_unlocked");
  const showInventory = isInventoryUnlocked(playerContext);
  const showExploration = hasFlag(playerContext, "exploration_unlocked");
  const showRetirement = getTotalElementAndSchoolExp(playerContext) >= 10000;
  const showCreation = hasGlobalFlag(playerContext, "newgameplus_unlocked");
  const showUpgrades = hasFlag(playerContext, "upgrades_unlocked");
  const showCreatures = hasFlag(playerContext, "creatures_unlocked");
  const showStrengthening =
    hasGlobalFlag(playerContext, "strengthening_unlocked") && showExploration;
  const showEnding = getEndingData(playerContext) != null;
  const showBestiary = hasGlobalFlag(playerContext, "bestiary_unlocked");

  return [
    "Campus",
    showResearch ? "Research" : null,
    showAutocast ? "Apprentices" : null,
    showInventory ? "Inventory" : null,
    showExploration ? "Exploration" : null,
    showCreatures ? "Creatures" : null,
    showUpgrades ? "Upgrades" : null,
    showStrengthening ? "Strengthen" : null,
    showEnding ? "Ending" : null,
    showCreation ? "Creation" : null,
    showRetirement ? "Retirement" : null,
    "Stats",
    showBestiary ? "Bestiary" : null,
    "Help",
    "Options",
    "About",
  ].filter((value) => value != null) as string[];
}

export function useGestureHandler(
  swipeLeftHandler: () => void,
  swipeRightHandler: () => void,
) {
  return useMemo(() => {
    if (Platform.OS === "web") {
      const leftGesture = Gesture.Fling()
        .direction(Directions.LEFT)
        .onEnd((e: any, success) => {
          if (success && e.translationX < 0) {
            swipeLeftHandler();
          }
        });
      const rightGesture = Gesture.Fling()
        .direction(Directions.RIGHT)
        .onEnd((e: any, success) => {
          if (success && e.translationX > 0) {
            swipeRightHandler();
          }
        });

      return Gesture.Race(leftGesture, rightGesture);
    }

    const POSITION_THRESHOLD = 80;
    const VELOCITY_THRESHOLD = 300;
    const Y_POSITION_THRESHOLD = 65;

    const sharedTouchPosition = {
      x: 0,
      y: 0,
      initialX: 0,
      initialY: 0,
      initialTimestamp: 0,
      lastTimestamp: 0,
      status: "end",
    };
    return Gesture.Manual()
      .runOnJS(true)
      .onTouchesDown((e, manager) => {
        if (sharedTouchPosition.status == "begin") {
          // Do nothing
          return;
        }

        sharedTouchPosition.initialX = e.allTouches[0].x;
        sharedTouchPosition.initialY = e.allTouches[0].y;

        sharedTouchPosition.x = e.allTouches[0].x;
        sharedTouchPosition.y = e.allTouches[0].y;

        sharedTouchPosition.initialTimestamp = Date.now();
        sharedTouchPosition.lastTimestamp = Date.now();
        sharedTouchPosition.status = "begin";
      })
      .onTouchesMove((e, manager) => {
        if (sharedTouchPosition.status != "begin") {
          return;
        }
        const currentTimestamp = Date.now();
        const timestampDeltaSec =
          Math.max(currentTimestamp - sharedTouchPosition.initialTimestamp, 1) /
          1000.0;
        const deltaX = e.allTouches[0].x - sharedTouchPosition.initialX;
        const deltaY = e.allTouches[0].y - sharedTouchPosition.initialY;
        const velocityX = deltaX / timestampDeltaSec;
        const velocityY = deltaY / timestampDeltaSec;
        const tooVertical = Math.abs(deltaY) > Y_POSITION_THRESHOLD;
        if (tooVertical) {
          return;
        }

        if (
          Math.abs(deltaY) * 2.5 > Math.abs(deltaX) &&
          Math.abs(deltaX) > POSITION_THRESHOLD
        ) {
          return;
        }

        if (
          velocityX < -VELOCITY_THRESHOLD &&
          e.allTouches[0].x - sharedTouchPosition.initialX < -POSITION_THRESHOLD
        ) {
          swipeLeftHandler();
          sharedTouchPosition.status = "end";
        } else if (
          velocityX > VELOCITY_THRESHOLD &&
          e.allTouches[0].x - sharedTouchPosition.initialX > POSITION_THRESHOLD
        ) {
          swipeRightHandler();
          sharedTouchPosition.status = "end";
        }
        sharedTouchPosition.x = e.allTouches[0].x;
        sharedTouchPosition.y = e.allTouches[0].y;
        sharedTouchPosition.lastTimestamp = currentTimestamp;
      })
      .onTouchesUp((e, manager) => {
        if (e.numberOfTouches === 0) {
          const currentTimestamp = Date.now();
          const timestampDeltaSec =
            Math.max(
              currentTimestamp - sharedTouchPosition.initialTimestamp,
              1,
            ) / 1000.0;
          const velocityX =
            (e.allTouches[0].x - sharedTouchPosition.initialX) /
            timestampDeltaSec;
          const velocityY =
            (e.allTouches[0].y - sharedTouchPosition.initialY) /
            timestampDeltaSec;
          const deltaX = e.allTouches[0].x - sharedTouchPosition.initialX;
          sharedTouchPosition.status = "end";
        }
      })
      .onTouchesCancelled((e, manager) => {
        sharedTouchPosition.status = "end";
      });
  }, [swipeLeftHandler, swipeRightHandler]);
}

export function useHorizontalScrollRef() {
  const elRef = useRef<any>();
  useEffect(() => {
    if (Platform.OS !== "web") {
      return;
    }
    const el = elRef.current;
    if (el) {
      const onWheel = (e: any) => {
        if (e.deltaY === 0) return;
        const scrollableElement = el.getScrollableNode();
        if (
          !(scrollableElement.scrollLeft === 0 && e.deltaY < 0) &&
          !(
            scrollableElement.scrollWidth -
              scrollableElement.clientWidth -
              Math.round(scrollableElement.scrollLeft) ===
              0 && e.deltaY > 0
          )
        ) {
          e.preventDefault();
          e.stopPropagation();
        }
        scrollableElement.scrollBy({ left: e.deltaY, behavior: "smooth" });
      };
      el.getScrollableNode().addEventListener("wheel", onWheel);
      return () =>
        el.getScrollableNode()?.removeEventListener?.("wheel", onWheel);
    }
  }, []);
  return elRef;
}

export const MAX_OPEN_TABS = 5;

export function useLayoutType() {
  const { width, height } = useWindowDimensions();
  return getLayoutType(height, width);
}

export function getLayoutType(height: number, width: number) {
  if (width < 700) {
    return {
      wideLayout: false,
      showMenu: false,
      showSpellMenu: false,
      showEventsMenu: false,
      activeContentTabs: 1,
    };
  }
  if (width < 960) {
    return {
      wideLayout: true,
      showMenu: true,
      showSpellMenu: false,
      showEventsMenu: false,
      activeContentTabs: 1,
    };
  }
  if (width < 1300) {
    return {
      wideLayout: true,
      showMenu: true,
      showSpellMenu: true,
      showEventsMenu: false,
      activeContentTabs: 1,
    };
  }
  return {
    wideLayout: true,
    showMenu: true,
    showSpellMenu: true,
    showEventsMenu: true,
    activeContentTabs: Math.min(
      MAX_OPEN_TABS,
      Math.max(1, Math.floor((width - 300 * 3) / 480)),
    ),
  };
}

export function getInitialActiveSectionObjects(
  state: PlayerContextState,
): { section: string; key: number }[] {
  const tabCount = MAX_OPEN_TABS;
  const menuItems = getAllMenuItems(state);
  return menuItems
    .slice(0, tabCount)
    .map((menuItem, idx) => ({ section: menuItem, key: idx }));
}

const styles = StyleSheet.create({
  effectText: {
    fontWeight: "bold",
  },
});
